import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MessageService } from '@klickdata/core/message';
import { QuickAccess } from '@klickdata/core/quick-access';
import { AppScope } from '@klickdata/core/resource';
import { User } from '@klickdata/core/user';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, first, merge, Observable, takeUntil } from 'rxjs';

@Component({
    selector: 'app-quick-access-listing',
    templateUrl: './quick-access-listing.component.html',
    styleUrls: ['./quick-access-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickAccessListingComponent extends OnDestroyHandler implements OnInit {
    @Input() user: User;
    @Input() customer: Customer;
    @Input() metadata: {
        [key: string]: any;
    };
    AppScope = AppScope;
    public quickAccessForm: FormGroup;
    public authCustomer$: Observable<Customer>;
    public authUser$: Observable<User>;
    public onDisableFavorite: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onDisableShortlisted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    constructor(protected auth: AuthService, protected fb: FormBuilder, protected message: MessageService) {
        super();
        this.authCustomer$ = this.auth.getCustomer();
        this.authUser$ = this.auth.getUser();
        this.quickAccessForm = this.fb.group({
            id: [null],
            user_quick_links: this.fb.array([]),
            admin_quick_links: this.fb.array([]),
            master_quick_links: this.fb.array([]),
        });
    }

    ngOnInit(): void {
        if (this.metadata) {
            this.updateQuickAccessForm();
        }
        merge(
            this.quickAccessForm.get('user_quick_links').valueChanges,
            this.quickAccessForm.get('admin_quick_links').valueChanges,
            this.quickAccessForm.get('master_quick_links').valueChanges
        )
            .pipe(takeUntil(this.destroy))
            .subscribe((value) => {
                // Calculate the total number of quick links and the total number of favorites
                const userLinks = this.quickAccessForm.get('user_quick_links').value as QuickAccess[];
                const adminLinks = this.quickAccessForm.get('admin_quick_links').value as QuickAccess[];
                const masterLinks = this.quickAccessForm.get('master_quick_links').value as QuickAccess[];

                const allLinks = [...(userLinks ?? []), ...(adminLinks ?? []), ...(masterLinks ?? [])];

                const favoriteCount = allLinks.filter((link) => link?.favorite === true).length;
                this.onDisableFavorite.next(favoriteCount > 2);
                const shortListedCount = allLinks.filter((link) => link?.navbar === true).length;
                this.onDisableShortlisted.next(shortListedCount > 2);
            });
    }

    updateQuickAccessForm(): void {
        this.quickAccessForm.patchValue(
            {
                id: this.user ? this.user.id : this.customer.id,
            },
            { emitEvent: false }
        );
        this.metadata.user_quick_links?.forEach((quickAccess) => {
            this.addQuickAccessFormGroup(quickAccess, this.quickAccessForm.get('user_quick_links') as FormArray);
        });
        this.metadata.admin_quick_links?.forEach((quickAccess) => {
            this.addQuickAccessFormGroup(quickAccess, this.quickAccessForm.get('admin_quick_links') as FormArray);
        });
        this.metadata.master_quick_links?.forEach((quickAccess) => {
            this.addQuickAccessFormGroup(quickAccess, this.quickAccessForm.get('master_quick_links') as FormArray);
        });
    }
    private addQuickAccessFormGroup(quickAccess: QuickAccess, formArray: FormArray): void {
        const formGroup = this.fb.group({
            label: [quickAccess.label],
            route: [quickAccess.route],
            favorite: [quickAccess.favorite],
            navbar: [quickAccess.navbar],
            enabled: [quickAccess.enabled],
            order: [quickAccess.order],
            default: [quickAccess.default],
            // platform: [quickAccess.platform],
        });
        formArray.push(formGroup, { emitEvent: false });
    }
}
